import { useState, type ReactNode } from 'react';
import { Skeleton, toast } from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { EmptyState, EmptyStateDescription, EmptyStateTitle } from '@strivelabs/ui-blocks/v2';
import { useTask, useUpdateTask } from '@/hooks/useTasks';
import { ANALYTICS_EVENTS, FEATURE_AREAS, useTrack, useTrackListFilter } from '@/lib/analytics';
import type { AttachedArtifact } from '@/types/artifact';
import type { Task, TaskPatch, TaskStatus, TenantUser } from '@/types/task';
import { TaskAttachments } from './TaskAttachments';
import { TaskDetailHead } from './TaskDetailHead';
import { TaskSection } from './TaskSection';
import { TaskPropertyRow } from './TaskPropertyRow';
import { TaskStatusSelect } from './TaskStatusSelect';
import { TaskPersonPicker } from './TaskPersonPicker';
import { TaskDatesRow } from './TaskDatesRow';
import { TaskLabelsEditor } from './TaskLabelsEditor';
import { TaskSourceRow } from './TaskSourceRow';
import { TaskActivityPanel } from './TaskActivityPanel';
import { TaskPerson } from '../TaskPerson';

/** The panel's two views. Overview edits the task; Activity is what happened to it. */
const TABS = [ 'overview', 'activity' ] as const;

type DetailTab = (typeof TABS)[number];

const TAB_LABELS: Record<DetailTab, string> = {
  overview: 'Overview',
  activity: 'Activity',
};

/** What a picked person looks like on the task, so the panel does not blank while saving. */
const asAssigned = (person: TenantUser | null) =>
  (person ? { id: person.id, display_name: person.display_name, image_url: person.image_url } : null);

interface TaskDetailBodyProps {
  taskId: string;
  /** Extra classes on the scrolling body — the fullscreen host centers it. */
  bodyClassName?: string;
  /**
   * Rendered under the properties: the footer differs by where the panel is
   * hosted. Handed the same `save` the fields use, so a footer action reports a
   * failure and is counted the same way rather than failing quietly.
   */
  footer?: (task: Task, save: (patch: TaskPatch) => void) => ReactNode;
  /**
   * How to open an attached artifact. Both hosts layer it over the task in
   * their own panel, with a back arrow to here.
   */
  onOpenArtifact: (artifact: AttachedArtifact) => void;
}

const DetailSkeleton = () => (
  <div className="flex flex-col gap-3" role="status" aria-busy="true" aria-label="Loading task">
    <Skeleton className="h-4 w-20" />
    <Skeleton className="h-7 w-3/4" />
    <Skeleton className="h-16 w-full" />
    <Skeleton className="h-6 w-1/2" />
    <Skeleton className="h-6 w-1/2" />
  </div>
);

/**
 * A task's details, editable in place.
 *
 * Every change is its own write, so nothing here has a save button: the panel
 * reads what the board holds and the mutation paints the change before the
 * server confirms it. Knows nothing about where it is mounted, which is what
 * lets the chat slot and the Tasks page share it.
 */
export const TaskDetailBody = ({
  taskId, bodyClassName, footer, onOpenArtifact,
}: TaskDetailBodyProps) => {
  const { data: task, isLoading, isError } = useTask(taskId);
  const { mutate: update } = useUpdateTask();
  const trackEvent = useTrack();
  const trackFilter = useTrackListFilter('tasks', FEATURE_AREAS.tasks);
  const [ tab, setTab ] = useState<DetailTab>('overview');

  if (isLoading && !task) {
    return <DetailSkeleton />;
  }

  if (isError || !task) {
    return (
      <EmptyState>
        <EmptyStateTitle>This task is no longer available</EmptyStateTitle>
        <EmptyStateDescription>
          It may have been deleted, or it belongs to another workspace.
        </EmptyStateDescription>
      </EmptyState>
    );
  }

  const save = (patch: TaskPatch, optimistic?: Partial<Task>) => {
    Object.keys(patch).forEach((field) =>
      trackEvent(ANALYTICS_EVENTS.TASK_UPDATED, { field, via: 'panel' }));

    update({ taskId, patch, optimistic }, {
      onError: (error: Error) => toast.error(error.message || 'Could not save that change'),
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={cn('min-h-0 flex-1 overflow-y-auto px-[18px] pb-[18px]', bodyClassName)}>
        {/* The head carries the panel's top inset rather than the scroll area,
            which cannot have one: the strip below pins at the top of the
            padding box, so an inset there is a band above the pinned strip
            with nothing covering it, and the scrolled rows show through. */}
        <div className="pt-[18px]">
          <TaskDetailHead
            taskKey={task.key}
            title={task.title}
            description={task.description}
            onTitleChange={(title) => save({ title })}
            onDescriptionChange={(description) => save({ description })}
          />
        </div>

        {/* Carries the rule under the head as well as the switch. The strip is
            hand-rolled for the same reason the rest of the board is: the shared
            tabs component brings its own scale and indicator. */}
        <div
          role="tablist"
          className="sticky top-0 z-[6] mb-1.5 flex gap-[22px] border-b border-[rgba(0,0,0,0.08)] bg-white pt-4"
        >
          {TABS.map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={tab === name}
              onClick={() => {
                setTab(name);
                trackFilter('tab', name);
              }}
              className={cn(
                '-mb-px border-b-2 pb-2.5 text-sm',
                tab === name
                  ? 'border-[#181818] font-semibold text-[#181818]'
                  : 'border-transparent font-normal text-[#9A9A9A] hover:text-[#696969]',
              )}
            >
              {TAB_LABELS[name]}
            </button>
          ))}
        </div>

        {tab === 'overview' ? (
          <>
          <TaskSection title="Properties">
            <TaskPropertyRow label="Status">
              <TaskStatusSelect
                value={task.status}
                onChange={(status: TaskStatus) => save({ status })}
              />
            </TaskPropertyRow>

            <TaskPropertyRow label="Creator">
              <TaskPerson person={task.creator} />
            </TaskPropertyRow>

            <TaskPropertyRow label="Assignee">
              <TaskPersonPicker
                label="Assignee"
                value={task.assignee}
                onChange={(person) =>
                  save({ assignee_user_id: person?.id ?? null }, { assignee: asAssigned(person) })}
              />
            </TaskPropertyRow>

            <TaskPropertyRow label="Reviewer">
              <TaskPersonPicker
                label="Reviewer"
                value={task.reviewer}
                clearable
                onChange={(person) =>
                  save({ reviewer_user_id: person?.id ?? null }, { reviewer: asAssigned(person) })}
              />
            </TaskPropertyRow>

            <TaskPropertyRow label="Dates">
              <TaskDatesRow
                startDate={task.start_date}
                dueDate={task.due_date}
                onChange={(dates) => save(dates)}
              />
            </TaskPropertyRow>

            <TaskPropertyRow label="Labels">
              <TaskLabelsEditor
                labels={task.labels}
                onChange={(names) => save({ labels: names })}
              />
            </TaskPropertyRow>

            <TaskPropertyRow label="Source">
              <TaskSourceRow source={task.source} />
            </TaskPropertyRow>
          </TaskSection>

          <TaskAttachments taskId={taskId} onOpenArtifact={onOpenArtifact} />
          </>
        ) : (
          <TaskActivityPanel taskId={taskId} />
        )}
      </div>

      {footer?.(task, save)}
    </div>
  );
};
